/**
 * Damage-type impulse profiles (PRD §5.4).
 *
 * Each profile feeds Rapier when a hit or kill event spawns chunks.
 * Determinism note: the *seed* picks variation within a profile so
 * the visual beats reproduce; resting positions may differ across
 * hardware and that's accepted per PRD §3.2.
 */

import type { Damage } from "../replay/events";

export interface ImpulseProfile {
  /** Linear impulse magnitude scale. */
  linear: number;
  /** Angular impulse magnitude scale. */
  angular: number;
  /** Cone half-angle in radians around the hit normal (0 = straight back). */
  cone: number;
  /** Upward bias: positive lifts debris (reactor breach), zero is sideways. */
  upBias: number;
  /** Number of chunks to spawn for this damage class. */
  chunkCount: number;
  /** Particle burst size (sparks/dust). */
  particles: number;
  /** Whether to lay a persistent ground decal. */
  decal: boolean;
}

export const IMPULSE_PROFILES: Record<Damage, ImpulseProfile> = {
  ballistic: {
    linear: 3.5,
    angular: 4,
    cone: Math.PI / 5,
    upBias: 0.3,
    chunkCount: 6,
    particles: 18,
    decal: true,
  },
  energy: {
    linear: 1.5,
    angular: 2.5,
    cone: Math.PI / 8,
    upBias: 0.1,
    chunkCount: 3,
    particles: 24, // brighter spark burst
    decal: true,
  },
  explosive: {
    linear: 5,
    angular: 6,
    cone: Math.PI,
    upBias: 0.6,
    chunkCount: 12,
    particles: 40,
    decal: true,
  },
  missile: {
    linear: 4,
    angular: 5,
    cone: Math.PI / 3,
    upBias: 0.4,
    chunkCount: 8,
    particles: 30,
    decal: true,
  },
  other: {
    linear: 2,
    angular: 3,
    cone: Math.PI / 4,
    upBias: 0.2,
    chunkCount: 4,
    particles: 10,
    decal: false,
  },
};

/** "Reactor breach" / kill profile — louder than any single-hit profile. */
export const REACTOR_BREACH: ImpulseProfile = {
  linear: 7,
  angular: 8,
  cone: Math.PI,
  upBias: 1.2,
  chunkCount: 24,
  particles: 80,
  decal: true,
};
